/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable @typescript-eslint/no-explicit-any */
import {
  ExerciseInput,
  MutationUpdateExerciseArgs,
  QuestionOutput,
  QuestionType,
} from '@/api/gql/graphql';
import { useGetQuizz } from '@/api/hooks/useQuizz';
import { createMenuItem } from '@/components/Common/DropdownMenu/MenuDropdown.utils';
import { QuizzFormValues, useHandleQuizzForm } from '@/hooks/useQuizzForm';
import { useToast } from '@/hooks/useToast';
import { useEffect, useCallback, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useQuestionLogic } from '@/hooks/useQuestionLogic';
import { useEvaluationStore } from '@/store/EvaluationStore/EvaluationStore';
import {
  EvaluationFormValues,
  useEvaluationForm,
} from '@/hooks/useEvaluationForm';
import { useGetSingleLessons } from '@/api/hooks/useLesson';
import {
  useCreateExercise,
  useDeleteExercise,
  useRemoveQuestion,
  useGetExercisesByLesson,
  useUpdateExercise,
} from '@/api/hooks/useExercise';
import { useCreateQuizz, useUpdateQuizz } from '@/api/hooks/useQuizz';
import { useFileUploadStore } from '@/store/FileUploadStore/FileUploadStore';
import axios from 'axios';
import { createFile } from '@/api/fileUpload';

export const useEvaluation = () => {
  // Store hooks
  const {
    setIsOpenModal,
    exerciceTabs,
    setExerciceTabs,
    activeTabExercise,
    setActiveTabExercise,
    exercice,
    setExercice,
    editingTab,
    setEditingTab,
    isModalConfirmationExerciseOpen,
    setIsModalConfirmationExerciseOpen,
    selectedEvaluation,
    setSelectedEvaluation,
    openEvaluationModal,
    openDeleteEvaluationModal,
    setOpenEvaluationModal,
    setOpenDeleteEvaluationModal,
    initialSelectedEvaluationOptions,
    isOpenEvaluationDeleteModal,
    setIsOpenEvaluationDeleteModal,
  } = useEvaluationStore();

  const { setSelectedFileName } = useFileUploadStore();

  // Hooks and state
  const { lessonId, moduleId } = useParams();
  const { showToast } = useToast();
  const [exerciseIdToDelete, setExerciseIdToDelete] = useState<string | null>(
    null,
  );
  const [isEditingQuestion, setIsEditingQuestion] = useState<boolean>(false);

  // Form hooks
  const { register, handleSubmit, setValue, errors, reset, getValues } =
    useHandleQuizzForm('create');

  const {
    register: registerEvaluation,
    handleSubmit: handleSubmitEvaluation,
    setValue: setValueEvaluation,
    errors: errorsEvaluation,
    reset: resetEvaluation,
  } = useEvaluationForm();

  // Question logic
  const {
    selectedType,
    setSelectedType,
    response1Check,
    setResponse1Check,
    response2Check,
    setResponse2Check,
    response3Check,
    setResponse3Check,
    response4Check,
    setResponse4Check,
    formatResponses,
    resetResponses,
  } = useQuestionLogic();

  // API hooks
  const { data: quizzList, isLoading: loadingQuizz } = useGetQuizz({
    lesson: lessonId,
    section: moduleId,
  });
  const { data: lesson } = useGetSingleLessons(lessonId || '');
  const { data: exercises, isLoading: loadingExercises } =
    useGetExercisesByLesson(lessonId || '');
  const { mutate: createExercise, isPending: loadingExerciseCreation } =
    useCreateExercise();
  const { mutate: deleteExercise, isPending: loadingDeleteExercise } =
    useDeleteExercise();
  const { mutate: updateExercise, isPending: loadingupdateQuestion } =
    useUpdateExercise();
  const { mutate: onDeleteQuestion, isPending: isDeleteQuestionPending } =
    useRemoveQuestion();
  const { mutate: createQuestion, isPending: isCreateQuestionPending } =
    useCreateQuizz();
  const { mutate: updateQuestion, isPending: loadingUpdateQuestion } =
    useUpdateQuizz();

  // Constants
  const QuizzQuestionType = [
    { _id: QuestionType.Qcm, name: QuestionType.Qcm },
    { _id: QuestionType.TrueOrFalse, name: QuestionType.TrueOrFalse },
    { _id: QuestionType.MissingWords, name: QuestionType.MissingWords },
  ];

  // Effects
  useEffect(() => {
    if (
      exercises?.exercisesByLesson &&
      exercises.exercisesByLesson.length > 0
    ) {
      const formattedExercises = exercises.exercisesByLesson.map(
        (exercise) => ({
          id: exercise._id,
          title: exercise.name,
          content: exercise.duration,
          duration: Number(exercise.duration),
        }),
      );
      setExerciceTabs(formattedExercises);
    } else {
      setExerciceTabs([]);
    }
  }, [exercises]);

  useEffect(() => {
    setSelectedEvaluation(null);
    setIsEditingQuestion(false);
  }, [activeTabExercise]);

  // Récupérer les questions de l'exercice actif
  const getActiveExerciseQuestions = () => {
    const activeExercise = exercises?.exercisesByLesson?.find(
      (exercise) => exercise._id === exerciceTabs[activeTabExercise]?.id,
    );
    return activeExercise?.questions || [];
  };

  // Handlers
  const handleSelectedQuizz = (quizz: QuestionOutput) => {
    setSelectedEvaluation(quizz);
  };

  const handleEditQuestion = (question: QuestionOutput) => {
    if (!question) return;
    setSelectedEvaluation(question);
    setIsEditingQuestion(true);
    setValue('name', question.name ?? '');
    setValue('description', question.description || '');
    setValue('type', question.type);
    setSelectedType(question.type);
    setSelectedFileName(question.image || '');

    if (question.responses) {
      question.responses.forEach((response, index) => {
        const responseNumber = index + 1;
        setValue(`reponse${responseNumber}` as any, response.response || '');
        if (responseNumber === 1) setResponse1Check(response.isRight);
        if (responseNumber === 2) setResponse2Check(response.isRight);
        if (responseNumber === 3) setResponse3Check(response.isRight);
        if (responseNumber === 4) setResponse4Check(response.isRight);
      });
    }
  };

  const handleEditTab = useCallback(
    (tabId: number) => {
      const exercice = exercises?.exercisesByLesson.find(
        (t) => t._id === tabId,
      );
      if (exercice) {
        setValueEvaluation('position', Number(exercice.position));
        setValueEvaluation('name', exercice.name);
        setValueEvaluation('duration', Number(exercice.duration));
        setExercice(exercice);
        setEditingTab(true);
        setIsOpenModal(true);
      }
    },
    [exerciceTabs, setValueEvaluation, setIsOpenModal],
  );

  const onSubmitEvaluation = handleSubmitEvaluation(
    (values: EvaluationFormValues) => {
      const newExercise: ExerciseInput = {
        lesson: lessonId || '',
        name: values.name,
        position: values.position,
        duration: values.duration?.toString(),
      };

      createExercise(newExercise, {
        onSuccess: () => {
          showToast('Exercice créé avec succès', 'success', {
            autoClose: 3000,
            position: 'top-right',
          });
          handleCloseModal();
          setActiveTabExercise(exerciceTabs.length);
        },
        onError: (error: { message: string }) => {
          showToast(error?.message, 'error', {
            autoClose: 3000,
            position: 'top-right',
          });
        },
      });
    },
  );

  const handleCloseModal = () => {
    resetEvaluation();
    setIsOpenModal(false);
    setExercice(null);
    setEditingTab(false);
  };

  const handleUpdateExercise = handleSubmitEvaluation(
    (values: EvaluationFormValues) => {
      const questionOutput: MutationUpdateExerciseArgs = {
        id: exercice?._id,
        input: {
          name: values.name ?? '',
          duration: values.duration?.toString(),
          position: values.position,
        },
      };

      updateExercise(questionOutput, {
        onSuccess: () => {
          showToast('Exercice modifié avec succès', 'success', {
            autoClose: 3000,
            position: 'top-right',
          });
          const updatedTabIndex = exerciceTabs.findIndex(
            (tab) => tab.id === exercice?._id,
          );
          if (updatedTabIndex !== -1) {
            setActiveTabExercise(updatedTabIndex);
          }
          handleCloseModal();
        },
        onError: (error: { message: string }) => {
          showToast(error?.message, 'error', {
            autoClose: 3000,
            position: 'top-right',
          });
        },
      });
    },
  );

  const handleConfirmExerciseAction = (exerciseId: number) => {
    setIsModalConfirmationExerciseOpen(true);
    setExerciseIdToDelete(exerciseId.toString());
  };

  const handleDeleteExercise = useCallback(() => {
    if (!exerciseIdToDelete) return;
    deleteExercise(exerciseIdToDelete, {
      onSuccess: () => {
        showToast('Exercice supprimé avec succès', 'success', {
          autoClose: 3000,
          position: 'top-right',
        });
        setIsModalConfirmationExerciseOpen(false);
        setExerciseIdToDelete(null);
        // Reset active tab if needed
        if (activeTabExercise >= exerciceTabs.length - 1) {
          setActiveTabExercise(Math.max(0, exerciceTabs.length - 2));
        }
      },
      onError: (error: { message: string }) => {
        showToast(error?.message, 'error', {
          autoClose: 3000,
          position: 'top-right',
        });
      },
    });
  }, [
    exerciseIdToDelete,
    deleteExercise,
    showToast,
    setIsModalConfirmationExerciseOpen,
    setExerciseIdToDelete,
    activeTabExercise,
    exerciceTabs.length,
    setActiveTabExercise,
  ]);

  const handleCreateQuestion = async (values: QuizzFormValues) => {
    try {
      const responses = formatResponses(values);
      const hasAtLeastOneTrue = responses.some(
        (response: { isRight: boolean }) => response.isRight,
      );

      if (selectedType) {
        const type = selectedType;
        if (!hasAtLeastOneTrue) {
          const warningMessage =
            type === QuestionType.Qcm || type === QuestionType.MissingWords
              ? `Pour un ${type}, il doit y avoir au moins une réponse correcte.`
              : 'Pour TRUE_OR_FALSE, il doit y avoir au moins une réponse correcte.';
          showToast(warningMessage, 'warning', {
            autoClose: 3000,
            position: 'top-right',
          });
          return;
        }
      }

      const activeExercise = exerciceTabs[activeTabExercise];
      if (!activeExercise) {
        showToast('Veuillez sélectionner un exercice', 'error', {
          autoClose: 3000,
          position: 'top-right',
        });
        return;
      }

      let imageUrl = null;
      if (values.picture && values.picture instanceof File) {
        try {
          const token = localStorage.getItem('authToken') ?? '';
          const response = await axios(createFile(values.picture, token));
          if (response.status !== 201) {
            throw new Error("Erreur lors de l'ajout de l'image");
          }
          imageUrl = response.data?.publicUrl || response.data;
        } catch (error) {
          showToast("Erreur lors de l'upload de l'image", 'error', {
            autoClose: 3000,
            position: 'top-right',
          });
          return;
        }
      }

      const questionData = {
        name: values.name ?? '',
        type: values?.type as QuestionType,
        responses,
        description: values.description,
        exercise: activeExercise.id,
        ...(imageUrl && { image: imageUrl }),
      };

      createQuestion(questionData, {
        onSuccess: () => {
          showToast('Question ajoutée', 'success', {
            autoClose: 3000,
            position: 'top-right',
          });
          reset();
          resetResponses();
          setSelectedType(null);
          setValue('name', '');
          setValue('description', '');
          setValue('type', undefined);
          setValue('reponse1', '');
          setValue('reponse2', '');
          setValue('reponse3', '');
          setValue('reponse4', '');
          setValue('picture', undefined);
          setResponse1Check(false);
          setResponse2Check(false);
          setResponse3Check(false);
          setResponse4Check(false);
          setSelectedFileName('');
        },
        onError: (error: { message: string }) => {
          showToast(error?.message, 'error', {
            autoClose: 3000,
            position: 'top-right',
          });
        },
      });
    } catch (error) {
      console.error('Error:', error);
      showToast(
        "Une erreur s'est produite lors de la création de la question",
        'error',
        {
          autoClose: 3000,
          position: 'top-right',
        },
      );
    }
  };

  const handleUpdateQuestion = async (values: QuizzFormValues) => {
    try {
      const responses = formatResponses(values);
      const hasAtLeastOneTrue = responses.some(
        (response: { isRight: boolean }) => response.isRight,
      );

      if (selectedType) {
        const type = selectedType;
        if (!hasAtLeastOneTrue) {
          const warningMessage =
            type === QuestionType.Qcm || type === QuestionType.MissingWords
              ? `Pour un ${type}, il doit y avoir au moins une réponse correcte.`
              : 'Pour TRUE_OR_FALSE, il doit y avoir au moins une réponse correcte.';
          showToast(warningMessage, 'warning', {
            autoClose: 3000,
            position: 'top-right',
          });
          return;
        }
      }

      const activeExercise = exerciceTabs[activeTabExercise];
      if (!activeExercise) {
        showToast('Veuillez sélectionner un exercice', 'error', {
          autoClose: 3000,
          position: 'top-right',
        });
        return;
      }

      let imageUrl = null;
      if (values.picture && values.picture instanceof File) {
        try {
          const token = localStorage.getItem('authToken') ?? '';
          const response = await axios(createFile(values.picture, token));
          if (response.status !== 201) {
            throw new Error("Erreur lors de l'ajout de l'image");
          }
          imageUrl = response.data?.publicUrl || response.data;
        } catch (error) {
          showToast("Erreur lors de l'upload de l'image", 'error', {
            autoClose: 3000,
            position: 'top-right',
          });
          return;
        }
      }

      updateQuestion(
        {
          updateQuestionId: selectedEvaluation?._id,
          input: {
            name: values.name ?? '',
            type: values?.type as QuestionType,
            responses,
            description: values.description,
            ...(imageUrl && { image: imageUrl }),
          },
        },
        {
          onSuccess: () => {
            showToast('Question modifiée', 'success', {
              autoClose: 3000,
              position: 'top-right',
            });
            setIsEditingQuestion(false);
            reset();
            resetResponses();
            setSelectedType(null);
            setValue('name', '');
            setValue('description', '');
            setValue('type', undefined);
            setValue('reponse1', '');
            setValue('reponse2', '');
            setValue('reponse3', '');
            setValue('reponse4', '');
            setResponse1Check(false);
            setResponse2Check(false);
            setResponse3Check(false);
            setResponse4Check(false);
            setSelectedEvaluation(null);
            setSelectedFileName('');
          },
          onError: (error: { message: string }) => {
            showToast(error?.message, 'error', {
              autoClose: 3000,
              position: 'top-right',
            });
          },
        },
      );
    } catch (error) {
      console.error('Error:', error);
      showToast(
        "Une erreur s'est produite lors de la modification de la question",
        'error',
        {
          autoClose: 3000,
          position: 'top-right',
        },
      );
    }
  };

  const createSingleQuestion = (values: QuizzFormValues) => {
    const responses = formatResponses(values);
    const hasAtLeastOneTrue = responses.some(
      (response: { isRight: boolean }) => response.isRight,
    );

    if (selectedType) {
      const type = selectedType;
      if (!hasAtLeastOneTrue) {
        const warningMessage =
          type === QuestionType.Qcm || type === QuestionType.MissingWords
            ? `Pour un ${type}, il doit y avoir au moins une réponse correcte.`
            : 'Pour TRUE_OR_FALSE, il doit y avoir au moins une réponse correcte.';
        showToast(warningMessage, 'warning', {
          autoClose: 3000,
          position: 'top-right',
        });
        return;
      }
    }

    const activeExercise = exerciceTabs[activeTabExercise];
    if (!activeExercise) {
      showToast('Veuillez sélectionner un exercice', 'error', {
        autoClose: 3000,
        position: 'top-right',
      });
      return;
    }

    const questionOutput: MutationUpdateExerciseArgs = {
      id: activeExercise.id,
      input: {
        questions: [
          {
            name: values.name ?? '',
            type: values?.type as QuestionType,
            responses,
            description: values.description,
          },
        ],
      },
    };

    updateExercise(questionOutput, {
      onSuccess: () => {
        showToast('Question ajoutée avec succès', 'success', {
          autoClose: 3000,
          position: 'top-right',
        });
        reset();
        setSelectedType(null);
        setValue('name', '');
        setValue('description', '');
        setValue('type', undefined);
        setValue('reponse1', '');
        setValue('reponse2', '');
        setValue('reponse3', '');
        setValue('reponse4', '');
        setValue('picture', undefined);
        setResponse1Check(false);
        setResponse2Check(false);
        setResponse3Check(false);
        setResponse4Check(false);
      },
      onError: (error: { message: string }) => {
        showToast(error?.message, 'error', {
          autoClose: 3000,
          position: 'top-right',
        });
      },
    });
  };

  const handleDeleteQuestion = () => {
    onDeleteQuestion(selectedEvaluation?._id, {
      onSuccess: () => {
        showToast('Question supprimée', 'success', {
          autoClose: 3000,
          position: 'top-right',
        });
        setIsOpenEvaluationDeleteModal(false);
      },
      onError: (error: { message: string }) => {
        showToast(error?.message, 'error', {
          autoClose: 3000,
          position: 'top-right',
        });
      },
    });
  };

  const getQuestionMenuItems = (question: QuestionOutput) => [
    createMenuItem('Modifier', 'font-[400]', () => {
      setIsEditingQuestion(true);
      handleEditQuestion(question);
    }),
    createMenuItem('Supprimer', 'font-[400] text-[#EA242C]', () => {
      setSelectedEvaluation(question);
      setIsOpenEvaluationDeleteModal(true);
    }),
  ];

  return {
    quizzList,
    lessonId,
    loadingQuizz,
    openEvaluationModal,
    handleSelectedQuizz,
    setSelectedEvaluation,
    selectedEvaluation,
    QuizzQuestionType,
    quizzMenuItems: selectedEvaluation
      ? getQuestionMenuItems(selectedEvaluation)
      : [],
    register,
    handleSubmit,
    setValue,
    errors,
    reset,
    createSingleQuestion,
    handleSelectedType: setSelectedType,
    selectedType,
    response1Check,
    setResponse1Check,
    response2Check,
    setResponse2Check,
    response3Check,
    setResponse3Check,
    response4Check,
    setResponse4Check,
    initialSelectedEvaluationOptions,
    isOpenEvaluationDeleteModal,
    openDeleteEvaluationModal,

    isDeleteQuestionPending,
    handleDeleteQuestion,
    registerEvaluation,
    handleSubmitEvaluation,
    setValueEvaluation,
    errorsEvaluation,
    onSubmitEvaluation,
    setActiveTabExercise,
    activeTabExercise,
    handleDeleteExercise,
    handleConfirmExerciseAction,
    handleEditTab,
    editingTab,
    lesson,
    loadingExerciseCreation,
    loadingupdateQuestion,
    createExercise,
    exercises,
    handleUpdateExercise,
    loadingExercises,
    exerciceTabs,
    exercice,
    handleCloseModal,
    isModalConfirmationExerciseOpen,
    setIsModalConfirmationExerciseOpen,
    setIsOpenEvaluationDeleteModal,
    loadingDeleteExercise,
    setOpenEvaluationModal,
    setOpenDeleteEvaluationModal,
    handleCreateQuestion,
    handleUpdateQuestion,
    handleEditQuestion,
    isEditingQuestion,
    isCreateQuestionPending,
    loadingUpdateQuestion,
    questions: getActiveExerciseQuestions(),
  };
};
